import type {
  IntegrationDescription,
  KeyValueDescription,
  ValueConfig,
  ValueConfig_V,
} from "@smartsuite/automation-engine-api";
import { SelectEditorDescription_Type } from "@smartsuite/automation-engine-api";
import type {
  ActionCandidate,
  InputCandidate,
  TriggerCandidate,
} from "../../../../../../../../../types/candidate";
import { StringEditor } from "../../StringEditor";
import { Checkbox, Icon, IconButton } from "@smartsuite/react-ui/lib";
import { t } from "i18next";
import {
  ArrowDivider,
  KeyValueItemContainer,
  KeyValueItemWrapper,
  RemoveButton,
  RequiredSection,
  RequiredText,
} from "./KeyValueItemStyles";
import { createDescription, createEditor } from "../utils/input";
import { SelectEditor } from "../../SelectEditor";
import type React from "react";
import { useState } from "react";

const getKeyFlexBasis = (
  onlyKeys: boolean,
  keyValueDescription?: KeyValueDescription,
): number => {
  if (onlyKeys) {
    return keyValueDescription?.withRequired === true ? 74 : 97;
  }
  if (keyValueDescription?.keyType?.oneofKind === "select") {
    return 45;
  }
  return 35;
};

const getValueFlexBasis = (
  onlyKeys: boolean,
  keyValueDescription?: KeyValueDescription,
): number => {
  if (onlyKeys) {
    return 0;
  }
  if (keyValueDescription?.keyType?.oneofKind === "select") {
    return 45;
  }
  return 62;
};

// The plain string a config holds, "" when unset; undefined when it holds
// anything else (references, multiple parts) and must not be treated as text.
const pickPlainString = (config?: ValueConfig): string | undefined => {
  if (config === undefined || config.values.length === 0) {
    return "";
  }
  if (config.values.length > 1) {
    return undefined;
  }
  const value = config.values[0].value;
  return value.oneofKind === "string" ? value.string : undefined;
};

const isKeyEmpty = (item: ValueConfig_V): boolean => {
  if (item.value.oneofKind !== "keyValue") {
    return true;
  }
  const key = item.value.keyValue.key;
  if (key?.values[0] === undefined) {
    return true;
  }
  if (key.values[0].value.oneofKind === "string") {
    return key.values[0].value.string === "";
  }
  return false;
};

export const KeyValueItem: React.FC<{
  onChange: (input: InputCandidate) => void;
  integrations: IntegrationDescription[];
  input: InputCandidate;
  item: ValueConfig_V;
  trigger: TriggerCandidate;
  actions: ActionCandidate[];
  action?: ActionCandidate;
  showValidationError: boolean;
  index: number;
  onClickTrash: (index: number) => void;
  onlyKeys: boolean;
  keyValueDescription?: KeyValueDescription;
}> = ({
  onChange,
  integrations,
  input,
  item,
  trigger,
  actions,
  action,
  showValidationError,
  index,
  onClickTrash,
  onlyKeys,
  keyValueDescription,
}) => {
  // The value StringEditor renders its content once, so remount it whenever a
  // key change writes a default value into it.
  const [valueEditorEpoch, setValueEditorEpoch] = useState(0);

  const keyTypeEditor = createEditor();

  switch (keyValueDescription?.keyType?.oneofKind) {
    case "text":
      keyTypeEditor.editor = {
        oneofKind: "text",
        text: keyValueDescription?.keyType.text,
      };
      break;
    case "select":
      keyTypeEditor.editor = {
        oneofKind: "select",
        select: {
          ...keyValueDescription.keyType.select,
          options: keyValueDescription?.keyType.select.options.filter((o) => {
            if (keyValueDescription?.uniqueKeys === true) {
              const isDuplicate = input.value?.values.some((value, i) => {
                if (i === index) {
                  return false;
                }
                if (value.value.oneofKind === "keyValue") {
                  const selectedKey =
                    value.value.keyValue.key?.values[0]?.value;
                  return (
                    selectedKey?.oneofKind === "string" &&
                    selectedKey.string === o.value
                  );
                }
                return false;
              });
              if (isDuplicate) {
                return false;
              }
            }
            return true;
          }),
        },
      };
      break;
  }

  // The value defaults to the selected key option's label for as long as the
  // user hasn't written their own: empty, or still the previous option's label.
  const defaultValueForKey = (
    previousKey: string | undefined,
    newKey: string | undefined,
    currentValue?: ValueConfig,
  ): string | undefined => {
    if (onlyKeys || keyValueDescription?.keyType?.oneofKind !== "select") {
      return undefined;
    }
    const options = keyValueDescription.keyType.select.options;
    const current = pickPlainString(currentValue);
    if (current === undefined) {
      return undefined;
    }
    const previousLabel = options.find((o) => o.value === previousKey)?.label;
    if (current !== "" && current !== previousLabel) {
      return undefined;
    }
    return options.find((o) => o.value === newKey)?.label;
  };

  const onChangeInputKey = (inp: InputCandidate): void => {
    input.value?.values.forEach((value, i) => {
      if (i !== index) {
        return;
      }
      if (value.value.oneofKind === "keyValue") {
        const previousKey = pickPlainString(value.value.keyValue.key);
        value.value.keyValue.key = inp.value;
        const defaultValue = defaultValueForKey(
          previousKey,
          pickPlainString(inp.value),
          value.value.keyValue.value,
        );
        if (defaultValue !== undefined) {
          value.value.keyValue.value = {
            values: [{ value: { oneofKind: "string", string: defaultValue } }],
          };
          setValueEditorEpoch((epoch) => epoch + 1);
        }
      }
    });
    onChange(input);
  };

  const onChangeInputValue = (inp: InputCandidate): void => {
    input.value?.values.forEach((value, i) => {
      if (i !== index) {
        return;
      }
      if (value.value.oneofKind === "keyValue") {
        value.value.keyValue.value = inp.value;
      }
    });
    onChange(input);
  };

  const onChangeRequired = (required: boolean): void => {
    input.value?.values.forEach((value, i) => {
      if (i !== index) {
        return;
      }
      if (value.value.oneofKind === "keyValue") {
        value.value.keyValue.required = required;
      }
    });
    onChange(input);
  };

  const onClickTrashEvent = (): void => {
    onClickTrash(index);
  };

  return (
    <KeyValueItemWrapper>
      <KeyValueItemContainer
        flexBasis={getKeyFlexBasis(onlyKeys, keyValueDescription)}
      >
        {keyTypeEditor?.editor?.oneofKind === "text" ? (
          <StringEditor
            input={{
              ...input,
              value:
                item.value.oneofKind === "keyValue"
                  ? item.value.keyValue.key
                  : undefined,
            }}
            onChange={onChangeInputKey}
            trigger={trigger}
            actions={actions}
            integrations={integrations}
            showValidationError={showValidationError}
            description={createDescription(input, keyTypeEditor)}
            disableReferences={true}
            isMultiple={false}
          />
        ) : (
          <SelectEditor
            input={{
              ...input,
              value:
                item.value.oneofKind === "keyValue"
                  ? item.value.keyValue.key
                  : undefined,
            }}
            onChange={onChangeInputKey}
            trigger={trigger}
            actions={actions}
            integrations={integrations}
            description={createDescription(input, keyTypeEditor)}
            type={SelectEditorDescription_Type.DROPDOWN}
            isMultiple={false}
            placeholder={keyValueDescription?.keyPlaceholder}
            showValidationError={showValidationError}
            isError={
              keyTypeEditor.required && isKeyEmpty(item) && showValidationError
            }
          />
        )}
      </KeyValueItemContainer>
      {!onlyKeys && keyTypeEditor?.editor.oneofKind === "select" && (
        <ArrowDivider>
          <Icon name="arrow-right-range" size="M" />
        </ArrowDivider>
      )}
      {!onlyKeys && (
        <KeyValueItemContainer
          flexBasis={getValueFlexBasis(onlyKeys, keyValueDescription)}
        >
          <StringEditor
            key={valueEditorEpoch}
            input={{
              ...input,
              value:
                item.value.oneofKind === "keyValue"
                  ? item.value.keyValue.value
                  : undefined,
            }}
            onChange={onChangeInputValue}
            trigger={trigger}
            actions={actions}
            action={action}
            integrations={integrations}
            showValidationError={showValidationError}
            description={createDescription(input)}
            isMultiple={false}
            placeholder={keyValueDescription?.valuePlaceholder}
          />
        </KeyValueItemContainer>
      )}
      {keyValueDescription?.withRequired === true && (
        <KeyValueItemContainer flexBasis={23}>
          <RequiredSection>
            <Checkbox
              ariaLabel={t("automation-form.field-required", "Required")}
              checked={
                item.value.oneofKind === "keyValue" &&
                item.value.keyValue.required === true
              }
              onChange={onChangeRequired}
            />
            <RequiredText>
              {t("automation-form.field-required", "Required")}
            </RequiredText>
          </RequiredSection>
        </KeyValueItemContainer>
      )}
      <KeyValueItemContainer flexBasis={3}>
        <RemoveButton>
          <IconButton
            name="trash-duotone"
            size="S"
            ariaLabel="Remove Button"
            color="currentColor"
            hoverColor="currentColor"
            onClick={onClickTrashEvent}
          />
        </RemoveButton>
      </KeyValueItemContainer>
    </KeyValueItemWrapper>
  );
};
